import { SetStateAction, useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { useTranslate } from 'sbx-crm-menu-ui';
import ButtonComponent from 'src/components/shared/ButtonComponent';
import { Checkbox } from 'src/components/ui/checkbox';
import { AppAgentContext } from 'src/context/ContextProvider';
import { appActions } from 'src/store';
import { ModalType } from 'src/types/Modal';
import { formatInitBot } from 'src/utils/botGenerator/formatScript2';
import { SelectComponent } from 'src/utils/UtilsComponent';
import useIgnoreRenderEffect from '../../../hooks/useIgnoreRenderEffect';
import { ActiveOptionAgent, OptionsUploadScript } from '../ai-agent-v2/agentSetupComponent/AgentDeployComponent';
import { getAgentData } from 'src/services/AgentService';
import { generateJsBotFileV3 } from 'src/utils';
import { stringFormat } from 'src/utils/botGenerator/formatScript';

type OptionsWithPreview = OptionsUploadScript & Record<'isPreview', boolean> & Record<'version', 'v1' | 'v2' | 'v3'>;

export const onLoginBot = (
    phone: string,
    name: string,
    email: string,
    countryCode: string,
    metadata: Record<string, any>
) => {
    const event = new CustomEvent('botEvent', {
        detail: {
            phone,
            name,
            email,
            countryCode,
            metadata,
            type: 'login',
        },
    });

    window.dispatchEvent(event);
};
export const valueAttachmentDefault = {
    pdf: false,
    image: false,
    excel: false,
    text: false,
    word: false,
    csv: false,
};

export const BotScript = () => {
    const {
        state: {
            session: {
                ai_agent_config: {
                    assesors,
                    folders: { scripts, css },
                },
                countries,
                domainName,
                origin,
            },
        },
        dispatch,
    } = useContext(AppAgentContext);
    const params = useParams();
    const { t: agent } = useTranslate('agent');
    const [values, setValues] = useState<OptionsWithPreview>({
        assessor: assesors?.[0].value,
        withWhatsApp: true,
        defaultCountry: '+57',
        withMicrophone: true,
        withAssessor: true,
        withAttachment: valueAttachmentDefault,
        withInitialMessage: true,
        isPreview: false,
        version: 'v3',
    });

    useIgnoreRenderEffect(() => {
        // console.log('BotScript component mounted with params:', params);
        window.addEventListener('onBotService', (evt) => {
            console.log(evt);
        });
    }, []);

    async function initBotV3() {
        const scriptv2 = document.getElementById('bot-script-v2');
        const divBotv2 = document.getElementById('ai-chatbot');
        scriptv2?.remove();
        divBotv2?.remove();
        const dataBot = await getAgentData(process.env.REACT_APP_BASE_URL || window.location.origin, params?.id || '0');
        const script = formatInitBot({
            baseUrl: process.env.REACT_APP_BASE_URL || window.location.origin,
            agentId: params?.id,
            withMicrophone: values.withMicrophone,
            withAssessor: values.withAssessor,
            withAttachment: values.withAttachment,
            withInitialMessage: values.withInitialMessage,
            dataBot,
            urlFiles: origin.concat('/www/', domainName, '/ai-agent/'),
        });

        const scriptElement = document.createElement('script');
        scriptElement.innerHTML = script;
        scriptElement.id = 'bot-script-v3';
        document.body.appendChild(scriptElement);
    }

    function initBotV2() {
        const scriptv3 = document.getElementById('bot-script-v3');
        const divBotv3 = document.getElementById(`sbx-bot-widget-${params?.id || ''}`);
        scriptv3?.remove();
        divBotv3?.remove();
        const script = stringFormat({
            baseUrl: process.env.REACT_APP_BASE_URL || window.location.origin,
            agentId: params?.id,
            assessorId: values.assessor,
            domainUrl: origin.concat('/www/', domainName, '/ai-agent/'),
            withWhatsApp: values.withWhatsApp,
            withMicrophone: values.withMicrophone,
            withAssessor: values.withAssessor,
            withAttachment: values.withAttachment,
            withInitialMessage: values.withInitialMessage,
            previewMode: values.isPreview,
            version: values.version,
            defaultCountry: values.defaultCountry || '+57',
            countries,
        });

        const scriptElement = document.createElement('script');
        scriptElement.innerHTML = script;
        scriptElement.id = 'bot-script-v2';
        document.body.appendChild(scriptElement);
    }

    useIgnoreRenderEffect(async () => {
        if (values.version === 'v3') {
            await initBotV3();
        } else {
            initBotV2();
        }
    }, [values]);

    const countriesOptions = useMemo(() => {
        return countries.map((country) => ({
            value: country.code,
            label: (
                <div className="flex items-center gap-2">
                    <span>
                        <img
                            src={`https://flagcdn.com/w40/${country.country.toLowerCase()}.png`}
                            width="40"
                            alt={country.name}
                        />
                    </span>
                    <span>{country.code}</span>
                </div>
            ),
        }));
    }, [countries]);

    const selectAssessorAndMore = () => {
        const assessors = assesors || [];
        dispatch(
            appActions.openModal({
                type: ModalType.DYNAMIC,
                data: {
                    title: agent('select_an_assessor'),
                    className:
                        '!bg-gradient-to-br !from-purple-50 !to-pink-50 !rounded-3xl !shadow-2xl w-full max-w-md !overflow-hidden z-[50]',
                    onClose() {
                        dispatch(appActions.closeModal(ModalType.DYNAMIC));
                    },
                    state: values,
                    children: (
                        state: OptionsWithPreview,
                        setState: React.Dispatch<SetStateAction<OptionsWithPreview>>
                    ) => {
                        const [activeAttachment, setActiveAttachment] = useState<boolean>(true);
                        return (
                            <div className="px-3">
                                <div className="relative">
                                    <SelectComponent
                                        label={''}
                                        options={assessors}
                                        value={state.assessor}
                                        onChange={(option) => setState({ ...state, assessor: option })}
                                    />
                                </div>

                                <div className="">
                                    <ActiveOptionAgent
                                        label="WhatsApp"
                                        description={agent('active_whatsapp_bot')}
                                        value={state.withWhatsApp}
                                        setValue={(val) => setState({ ...state, withWhatsApp: val })}
                                    />
                                    {state.withWhatsApp && (
                                        <Select
                                            className="mt-2"
                                            options={countriesOptions}
                                            value={
                                                countriesOptions.find(
                                                    (co) => co.value === state.defaultCountry || ''
                                                ) || null
                                            }
                                            onChange={(option) => {
                                                setState({ ...state, defaultCountry: option?.value || '' });
                                            }}
                                            placeholder={agent('select_default_country')}
                                        />
                                    )}
                                    <ActiveOptionAgent
                                        label={agent('microphone')}
                                        description={agent('active_microphone_bot')}
                                        value={state.withMicrophone}
                                        setValue={(val) => setState({ ...state, withMicrophone: val })}
                                    />
                                    <ActiveOptionAgent
                                        label={agent('assessor')}
                                        description={agent('active_assessor_bot')}
                                        value={state.withAssessor}
                                        setValue={(val) => setState({ ...state, withAssessor: val })}
                                    />
                                    <ActiveOptionAgent
                                        label={agent('attachment')}
                                        description={agent('active_attachment_bot')}
                                        value={activeAttachment}
                                        setValue={(val) => {
                                            if (!val) {
                                                setState({
                                                    ...state,
                                                    withAttachment: valueAttachmentDefault,
                                                });
                                            }
                                            setActiveAttachment(val);
                                        }}
                                    />
                                    {activeAttachment && (
                                        <div className="grid grid-cols-3 gap-4 mt-2">
                                            <div className="flex items-center gap-1">
                                                <Checkbox
                                                    id="images"
                                                    onClick={() =>
                                                        setState({
                                                            ...state,
                                                            withAttachment: {
                                                                ...state.withAttachment,
                                                                image: !state.withAttachment.image,
                                                            },
                                                        })
                                                    }
                                                    checked={state.withAttachment.image}
                                                />
                                                <label htmlFor="images" className="text-sm">
                                                    Images
                                                </label>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Checkbox
                                                    id="pdf-file"
                                                    onClick={() =>
                                                        setState({
                                                            ...state,
                                                            withAttachment: {
                                                                ...state.withAttachment,
                                                                pdf: !state.withAttachment.pdf,
                                                            },
                                                        })
                                                    }
                                                    checked={state.withAttachment.pdf}
                                                />
                                                <label htmlFor="pdf-file" className="text-sm">
                                                    PDF
                                                </label>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Checkbox
                                                    id="excel-file"
                                                    onClick={() =>
                                                        setState({
                                                            ...state,
                                                            withAttachment: {
                                                                ...state.withAttachment,
                                                                excel: !state.withAttachment.excel,
                                                            },
                                                        })
                                                    }
                                                    checked={state.withAttachment.excel}
                                                />
                                                <label htmlFor="excel-file" className="text-sm">
                                                    Excel
                                                </label>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Checkbox
                                                    id="csv-file"
                                                    onClick={() =>
                                                        setState({
                                                            ...state,
                                                            withAttachment: {
                                                                ...state.withAttachment,
                                                                csv: !state.withAttachment.csv,
                                                            },
                                                        })
                                                    }
                                                    checked={state.withAttachment.csv}
                                                />
                                                <label htmlFor="csv-file" className="text-sm">
                                                    CSV
                                                </label>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Checkbox
                                                    id="text-file"
                                                    onClick={() =>
                                                        setState({
                                                            ...state,
                                                            withAttachment: {
                                                                ...state.withAttachment,
                                                                text: !state.withAttachment.text,
                                                            },
                                                        })
                                                    }
                                                    checked={state.withAttachment.text}
                                                />
                                                <label htmlFor="text-file" className="text-sm">
                                                    Text
                                                </label>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Checkbox
                                                    id="word-file"
                                                    onClick={() =>
                                                        setState({
                                                            ...state,
                                                            withAttachment: {
                                                                ...state.withAttachment,
                                                                word: !state.withAttachment.word,
                                                            },
                                                        })
                                                    }
                                                    checked={state.withAttachment.word}
                                                />
                                                <label htmlFor="word-file" className="text-sm">
                                                    Word
                                                </label>
                                            </div>
                                        </div>
                                    )}

                                    <ActiveOptionAgent
                                        label={agent('initial_message')}
                                        description={agent('active_initial_message_bot')}
                                        value={state.withInitialMessage}
                                        setValue={(val) => setState({ ...state, withInitialMessage: val })}
                                    />
                                    <ActiveOptionAgent
                                        label={'Is Preview'}
                                        description={'mode preview'}
                                        value={state.isPreview}
                                        setValue={(val) => setState({ ...state, isPreview: val })}
                                    />
                                    <div className="flex justify-between items-center mt-4">
                                        <div className="flex items-center gap-2">
                                            <label htmlFor="">Version</label>
                                        </div>
                                        <SelectComponent
                                            options={[
                                                {
                                                    label: 'v1',
                                                    value: 'v1',
                                                },
                                                {
                                                    label: 'v2',
                                                    value: 'v2',
                                                },
                                                {
                                                    label: 'v3',
                                                    value: 'v3',
                                                },
                                            ]}
                                            value={state.version}
                                            onChange={(option) => setState({ ...state, version: option })}
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end mt-5">
                                    <ButtonComponent
                                        label={'Change config'}
                                        className="primary-gradient-button"
                                        disabled={
                                            !state ||
                                            (Object.values(state.withAttachment).every((v) => !v) && activeAttachment)
                                        }
                                        onClick={() => {
                                            setValues(state);
                                            dispatch(appActions.closeModal(ModalType.DYNAMIC));
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    },
                },
            })
        );
    };

    return (
        <div className="relative h-screen flex items-start justify-start gap-2">
            <button
                className="btn btn-sm btn-primary"
                type="button"
                onClick={() => {
                    localStorage.removeItem(`twilio-chat-session-${params?.id}-v2`);
                    localStorage.removeItem(`twilio-chat-session-${params?.id}`);
                    window.location.reload();
                }}
            >
                Clear session and reload
            </button>
            <button
                className="btn btn-sm btn-primary"
                type="button"
                onClick={() => {
                    localStorage.removeItem(`twilio-chat-session-${params?.id}-v2`);
                    localStorage.removeItem(`twilio-chat-session-${params?.id}`);
                    window.location.href = `/ai-studio/#/agent_setup/${params?.id}`;
                }}
            >
                Go to Agent
            </button>
            <button
                className="btn btn-sm btn-primary"
                type="button"
                onClick={() => {
                    selectAssessorAndMore();
                }}
            >
                Config
            </button>

            <button
                className="btn btn-sm btn-primary"
                type="button"
                onClick={() => {
                    onLoginBot('3006666666', 'Hans Ospina', 'hans21@socobox.co', '+1', {
                        acc_manager: 'jung.houck@ibuyflowers.com',
                        customer: '31de4734-f71b-466d-8f53-a76c8e98fb60',
                        is_sales_manager: false,
                        country: 'US',
                    });
                }}
            >
                Test login
            </button>

            <ButtonComponent
                label={'Upload Bot Test'}
                className="primary-gradient-button"
                onClick={() => {
                    generateJsBotFileV3(
                        origin.concat('/www/', domainName, '/ai-agent/'),
                        params?.id || '',
                        css,
                        scripts,
                        values.withMicrophone,
                        values.withAssessor,
                        values.withAttachment,
                        values.withInitialMessage,
                        () => {}
                    );
                }}
            />
        </div>
    );
};
